import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMolecule } from '@peruselab/api-client/molecules';
import { getIsomer } from '@peruselab/api-client/isomers';
import { SvgMarkup } from './SvgMarkup';
import { isomerSvg } from './isomerSvg';

interface IsomerThumbnailProps {
  readonly moleculeId: string;
  readonly isomerId: string;
  readonly size?: number;
}

type ThumbnailState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly svg: string }
  | { readonly status: 'error'; readonly message: string };

// #128/#130: a read-only structure thumbnail for a picked Isomer. An Isomer has no depiction of
// its own, so this fetches its Molecule (graph/bonds) and its own stereochemistry, generates a
// fresh 2D layout -- including correctly rendered wedge/hash bonds (#130) -- and renders it with
// the same SVG export pipeline the "Export SVG" button uses -- no pointer/selection/undo
// interaction, purely a display component.
export function IsomerThumbnail({ moleculeId, isomerId, size = 96 }: IsomerThumbnailProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<ThumbnailState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    (async () => {
      const [molecule, isomer] = await Promise.all([getMolecule(moleculeId), getIsomer(isomerId)]);
      // #429 -- namespace this inlined SVG's <marker> ids (see MoleculeThumbnail).
      const svg = await isomerSvg(molecule.document, isomer.document, `i-${moleculeId}-${isomerId}-`);
      if (!cancelled) setState({ status: 'ready', svg });
    })().catch((error: unknown) => {
      if (!cancelled) {
        setState({
          status: 'error',
          message: error instanceof Error ? error.message : t('isomerThumbnail.renderError')
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [moleculeId, isomerId]);

  return (
    <div className="isomer-thumbnail" style={{ width: size, height: size }}>
      {state.status === 'loading' ? <span className="isomer-thumbnail-status">{t('common.loading')}</span> : null}
      {state.status === 'error' ? (
        <span className="isomer-thumbnail-status isomer-thumbnail-error">{state.message}</span>
      ) : null}
      {state.status === 'ready' ? (
        // The injected markup is generated entirely from drawingDocumentToSvg -- numeric
        // coordinates and fixed element symbols, no user-controlled text -- the same trust level
        // as the existing SVG-export download path.
        <SvgMarkup className="isomer-thumbnail-svg" svg={state.svg} />
      ) : null}
    </div>
  );
}
